// Port of FloodDataManager.swift for the NOAA National Water Model inundation
// extent. Camera-driven: schedule(center, distance) clears everything beyond
// 100 km, otherwise builds the capped, grid-snapped request and skips it when
// the previous fetch already covers the box at equal or finer detail.
import { bboxCovers, buildFIMRequest, ESRIError, fetchFIM, type FIMCollection, type FIMRequest } from '../api/fim';
import { CAMERA, METERS_PER_DEGREE_LAT } from '../config/constants';
import { isAbortError } from '../lib/fetchJson';
import type { LatLng, Region } from '../lib/geo';

export interface FIMState {
  polygons: FIMCollection;
  isLoading: boolean;
  error: string | null;
  /** True when the camera is too far out for FIM to be active. */
  tooFarOut: boolean;
}

const EMPTY: FIMCollection = { type: 'FeatureCollection', features: [] };

type Listener = () => void;

export class FIMStore {
  private state: FIMState = { polygons: EMPTY, isLoading: false, error: null, tooFarOut: true };
  private lastFetch: FIMRequest | null = null;
  private abort: AbortController | null = null;
  private listeners = new Set<Listener>();

  constructor(private readonly load: (url: string, signal: AbortSignal) => Promise<FIMCollection> = fetchFIM) {}

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): FIMState => this.state;

  private set(patch: Partial<FIMState>) {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }

  /** CombinedMapView.scheduleFIMFetch. */
  schedule(center: LatLng, distance: number): void {
    if (distance > CAMERA.fimMaxVisibilityDistance) {
      this.clear(true);
      return;
    }
    const span = Math.max(CAMERA.minFIMSpanDegrees, (distance / METERS_PER_DEGREE_LAT) * 1.5);
    this.fetchFloodData({ center, latDelta: span, lonDelta: span * 1.5 });
  }

  fetchFloodData(region: Region): void {
    const request = buildFIMRequest(region);
    if (this.lastFetch && bboxCovers(this.lastFetch.bbox, request.bbox) && this.lastFetch.maxAllowableOffset <= request.maxAllowableOffset) {
      if (this.state.tooFarOut) this.set({ tooFarOut: false });
      return;
    }
    this.abort?.abort();
    const ac = new AbortController();
    this.abort = ac;
    this.set({ isLoading: true, error: null, tooFarOut: false });
    this.load(request.url, ac.signal)
      .then((fc) => {
        if (ac.signal.aborted) return;
        this.lastFetch = request;
        this.set({ polygons: fc, isLoading: false, error: null });
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted || isAbortError(err)) return;
        const message = err instanceof ESRIError ? `Server error: ${err.message}` : err instanceof Error ? err.message : 'Failed to load';
        this.set({ isLoading: false, error: message });
      });
  }

  clear(tooFarOut = false): void {
    this.abort?.abort();
    this.abort = null;
    this.lastFetch = null;
    this.set({ polygons: EMPTY, isLoading: false, error: null, tooFarOut });
  }
}

export const fimStore = new FIMStore();
